import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Edit } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface EditProviderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: {
    id: string;
    provider: { 
      nombre: string; 
      tipo: string;
      telefono?: string;
      email?: string;
    };
    descripcion_servicio: string;
    precio: number;
    pagado: number;
    notas?: string;
  } | null;
  onProviderUpdated: (providerId: string, updatedData: any) => void;
}

export const EditProviderModal: React.FC<EditProviderModalProps> = ({
  open,
  onOpenChange,
  provider,
  onProviderUpdated
}) => {
  const [formData, setFormData] = useState({
    descripcion_servicio: '',
    precio: '',
    notas: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (provider) {
      setFormData({
        descripcion_servicio: provider.descripcion_servicio,
        precio: provider.precio.toString(),
        notas: provider.notas || ''
      });
    }
  }, [provider]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!provider) return;

    const precio = parseFloat(formData.precio);
    if (isNaN(precio) || precio <= 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Por favor ingresa un precio válido"
      });
      return;
    }

    if (precio < provider.pagado) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "El precio no puede ser menor al monto ya pagado"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedData = {
        descripcion_servicio: formData.descripcion_servicio,
        precio: precio,
        notas: formData.notas
      };

      onProviderUpdated(provider.id, updatedData);
      
      toast({
        title: "Proveedor actualizado",
        description: "Los datos del proveedor se han actualizado correctamente"
      });

      onOpenChange(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo actualizar el proveedor"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Edit className="w-5 h-5 mr-2" />
            Editar Proveedor
          </DialogTitle>
          <DialogDescription>
            Modifica la información del servicio contratado con este proveedor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Provider Info (Read-only) */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium">{provider.provider.nombre}</h3>
            <p className="text-sm text-gray-600">{provider.provider.tipo}</p>
            {provider.provider.telefono && (
              <p className="text-sm text-gray-600">Tel: {provider.provider.telefono}</p>
            )}
            {provider.provider.email && (
              <p className="text-sm text-gray-600">Email: {provider.provider.email}</p>
            )}
          </div>

          {/* Payment Status */}
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Monto Pagado:</span>
              <span className="text-green-600 font-bold">{formatCurrency(provider.pagado)}</span>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              El nuevo precio debe ser igual o mayor al monto pagado
            </p>
          </div>

          {/* Edit Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="descripcion_servicio">Descripción del Servicio</Label>
              <Textarea
                id="descripcion_servicio"
                value={formData.descripcion_servicio}
                onChange={(e) => setFormData({...formData, descripcion_servicio: e.target.value})}
                placeholder="Describe el servicio contratado..."
                rows={3}
                required
              />
            </div>

            <div>
              <Label htmlFor="precio">Precio del Servicio</Label>
              <Input
                id="precio"
                type="number"
                step="0.01"
                min={provider.pagado}
                value={formData.precio}
                onChange={(e) => setFormData({...formData, precio: e.target.value})}
                placeholder="0.00"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Mínimo: {formatCurrency(provider.pagado)}
              </p>
            </div>

            <div>
              <Label htmlFor="notas">Notas Adicionales</Label>
              <Textarea
                id="notas"
                value={formData.notas}
                onChange={(e) => setFormData({...formData, notas: e.target.value})}
                placeholder="Condiciones especiales, horarios, etc..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};